import React, { useEffect, useRef, useState } from 'react';
import { getFooterUrl, getHeaderFooterOrigin, HEADER_FOOTER_STYLESHEET_PATH, rewriteHeaderFooterAssetUrl } from '../services/configService';

/**
 * Footer - A consistent footer component for the application.
 *
 * Fetches footer HTML from a remote URL and displays it with complete style isolation
 * using Shadow DOM. This ensures that the app's styling cannot impact the footer's
 * HTML/CSS and vice versa.
 *
 * The component uses Shadow DOM to create a completely isolated styling context,
 * preventing any CSS leakage between the app and the footer content.
 *
 * You can provide custom styles via the `customStyles` prop, which will be injected
 * into the Shadow DOM and only affect the footer content, maintaining full isolation.
 *
 * @param customStyles - Optional CSS string to inject into the footer's Shadow DOM.
 *                       These styles will only affect the footer and won't leak to the app.
 *
 * @example
 * ```tsx
 * <Footer />
 * ```
 *
 * @example
 * ```tsx
 * <Footer customStyles={`
 *   footer {
 *     background-color: #custom-color !important;
 *     padding: 20px;
 *   }
 *   .navigation a {
 *     color: #fff;
 *   }
 * `} />
 * ```
 */
interface FooterProps {
  customStyles?: string;
}

const Footer: React.FC<FooterProps> = ({ customStyles }) => {
  const remoteStylesheetUrl = `${getHeaderFooterOrigin()}${HEADER_FOOTER_STYLESHEET_PATH}`;
  const stylesheetBaseUrl = `${getHeaderFooterOrigin()}/wp-content/themes/mindful-therapy-group/css/`;
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stylesheetLoadedRef = useRef(false);
  const footerFetchedRef = useRef(false);

  // Function to fetch and inject the remote stylesheet into Shadow DOM
  const injectRemoteStylesheet = React.useCallback(async () => {
    if (!shadowRootRef.current || stylesheetLoadedRef.current) return;

    // Check if stylesheet is already loaded
    const existingStylesheet = shadowRootRef.current.querySelector('style[data-remote-footer-stylesheet]');
    if (existingStylesheet) {
      stylesheetLoadedRef.current = true;
      return;
    }

    try {
      // Fetch the CSS content
      const response = await fetch(remoteStylesheetUrl);
      if (!response.ok) {
        console.warn('Failed to fetch remote stylesheet:', response.status);
        return;
      }

      let cssContent = await response.text();

      // Handle @import statements - convert relative URLs to absolute
      cssContent = cssContent.replace(
        /@import\s+["']([^"']+)["']/g,
        (match, url) => {
          if (url.startsWith('http') || url.startsWith('//')) {
            return match; // Already absolute
          }
          // Convert relative URL to absolute
          const baseUrl = stylesheetBaseUrl;
          const absoluteUrl = new URL(url, baseUrl).href;
          return `@import "${absoluteUrl}"`;
        }
      );

      // Create and inject the style tag with CSS content
      const styleTag = document.createElement('style');
      styleTag.setAttribute('data-remote-footer-stylesheet', 'true');
      styleTag.textContent = cssContent;
      
      // Insert at the beginning so our custom styles can override if needed
      if (shadowRootRef.current.firstChild) {
        shadowRootRef.current.insertBefore(styleTag, shadowRootRef.current.firstChild);
      } else {
        shadowRootRef.current.appendChild(styleTag);
      }
      
      stylesheetLoadedRef.current = true;
    } catch (err) {
      console.error('Error loading remote stylesheet:', err);
    }
  }, []);

  // Function to inject default footer-scoped styles
  const injectDefaultFooterStyles = React.useCallback(() => {
    if (!shadowRootRef.current) return;

    // Remove any existing default footer styles
    const existingDefaultStyle = shadowRootRef.current.querySelector('style[data-default-footer-styles]');
    if (existingDefaultStyle) {
      existingDefaultStyle.remove();
    }

    // Inject default footer-scoped styles (font-size override)
    const defaultStyleTag = document.createElement('style');
    defaultStyleTag.setAttribute('data-default-footer-styles', 'true');
    defaultStyleTag.textContent = `
      footer {
        font-size: 14px;
      }
    `;
    shadowRootRef.current.appendChild(defaultStyleTag);
  }, []);

  // Function to inject custom styles into Shadow DOM
  const injectCustomStyles = React.useCallback(() => {
    if (!shadowRootRef.current) return;

    // Remove any existing custom styles
    const existingCustomStyle = shadowRootRef.current.querySelector('style[data-custom-footer-styles]');
    if (existingCustomStyle) {
      existingCustomStyle.remove();
    }

    // Inject custom styles if provided (these will only affect the footer in Shadow DOM)
    // Custom styles are injected after the remote stylesheet so they can override if needed
    if (customStyles) {
      const customStyleTag = document.createElement('style');
      customStyleTag.setAttribute('data-custom-footer-styles', 'true');
      customStyleTag.textContent = customStyles;
      shadowRootRef.current.appendChild(customStyleTag);
    }
  }, [customStyles]);

  // Fetch footer content (only once on mount)
  useEffect(() => {
    // Prevent double-fetching in React StrictMode
    if (footerFetchedRef.current) return;
    footerFetchedRef.current = true;

    const fetchFooter = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(getFooterUrl());
        
        if (!response.ok) {
          throw new Error(`Failed to fetch footer: ${response.status} ${response.statusText}`);
        }

        // The API returns a JSON string, so we need to parse it first
        const jsonText = await response.text();
        let htmlContent: string;
        
        try {
          // Parse the JSON to get the actual HTML string
          htmlContent = JSON.parse(jsonText);
        } catch {
          // If it's not valid JSON, use the text directly
          htmlContent = jsonText;
        }

        // After JSON parsing, replace any remaining literal escape sequences
        // (in case of double-encoding or if JSON.parse didn't handle them)
        htmlContent = htmlContent
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t');

        // Initialize Shadow DOM if not already created
        if (containerRef.current && !shadowRootRef.current) {
          shadowRootRef.current = containerRef.current.attachShadow({ mode: 'open' });
        }

        if (shadowRootRef.current) {
          // Clear previous content
          shadowRootRef.current.innerHTML = '';
          stylesheetLoadedRef.current = false; // Reset flag

          // FIRST: Load the remote stylesheet BEFORE inserting footer HTML
          // This ensures styles are ready when the HTML is added
          await injectRemoteStylesheet();

          // Create a temporary container to parse the HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;

          // Extract and inject any <style> tags from the HTML into the shadow DOM
          const styleTags = tempDiv.querySelectorAll('style');
          styleTags.forEach((styleTag) => {
            const newStyle = document.createElement('style');
            newStyle.textContent = styleTag.textContent;
            shadowRootRef.current!.appendChild(newStyle);
            styleTag.remove();
          });

          // Extract and inject any <link> tags for stylesheets from the HTML
          const linkTags = tempDiv.querySelectorAll('link[rel="stylesheet"]');
          linkTags.forEach((linkTag) => {
            const newLink = document.createElement('link');
            newLink.rel = 'stylesheet';
            const href = linkTag.getAttribute('href') || '';
            newLink.href = rewriteHeaderFooterAssetUrl(href);
            shadowRootRef.current!.appendChild(newLink);
            linkTag.remove();
          });

          // Remove script tags and other non-footer content
          tempDiv.querySelectorAll('script').forEach((script) => script.remove());
          
          // Check if footer already exists in shadow DOM (prevent duplicate rendering)
          const existingFooter = shadowRootRef.current.querySelector('footer');
          if (!existingFooter) {
            // Extract only the footer element
            const footerElement = tempDiv.querySelector('footer');
            
            if (footerElement) {
              // Clone and append the footer content to shadow DOM
              // Styles are already loaded, so they should apply immediately
              const footerClone = footerElement.cloneNode(true);
              shadowRootRef.current.appendChild(footerClone);
            } else {
              // Fallback: if no footer element found, use the entire content
              const contentClone = tempDiv.cloneNode(true);
              shadowRootRef.current.appendChild(contentClone);
            }
          }

          // Inject default footer-scoped styles (font-size override)
          injectDefaultFooterStyles();

          // Inject custom styles AFTER footer HTML and remote stylesheet are loaded
          injectCustomStyles();
        }
      } catch (err) {
        console.error('Error fetching footer:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load footer';
        // Use setTimeout to ensure state updates happen in the next tick,
        // which allows React Testing Library to properly track them
        setTimeout(() => {
          setError(errorMessage);
          setIsLoading(false);
        }, 0);
      } finally {
        // Use setTimeout to ensure state updates happen in the next tick,
        // which allows React Testing Library to properly track them
        setTimeout(() => {
          setIsLoading(false);
        }, 0);
      }
    };

    fetchFooter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle custom styles updates (re-inject when customStyles prop changes)
  useEffect(() => {
    if (!shadowRootRef.current) return;
    injectCustomStyles();
  }, [injectCustomStyles]);

  return (
    <div 
      ref={containerRef}
      style={{
        // Minimal container styling - won't affect shadow DOM content
        width: '100%',
        display: 'block'
      }}
    >
      {isLoading && (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading footer...
        </div>
      )}
      {error && (
        <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
          Error loading footer: {error}
        </div>
      )}
    </div>
);
};

export default Footer;